import { fetchBillPayments, fetchFavourites, fetchTopProducts } from '@api/useQueryCalls';
import { lainnya } from '@assets/png/imagesPng';
import AddFavProducts from '@atoms/AddFavProducts';
import FavouriteHeader from '@atoms/FavouriteHeader';
import ShimmerBPTile from '@atoms/Shimmers/ShimmerBPTile';
import { View, Text } from '@components/primitives';
import useFeatureFlag from '@customHooks/useFeatureFlag';
import { Ionicons } from '@expo/vector-icons';
import BPProductTile from '@molecules/BPProductTile';
import { useIsFocused } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { FEATURE_FLAGS } from '@root/constants';
import colors from '@theme/colors';
import { scale } from '@utils/normalize';
import React, { useEffect, useCallback, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList } from 'react-native';
import styles from './styles';
import { transformProducts } from './topProductUtils';

interface Product {
  icon: any;
  name: string;
  screenName: string;
  id: number;
  availability: boolean;
}

interface ProductTileProps {
  item: Product;
  isEditEnabled: boolean;
  showFavourites?: boolean;
  isAddEnabled?: boolean;
}

function fetchTopOrFavourites(showFavourites: any): {
  isLoading: any;
  isError: any;
  data: any;
  isSuccess: any;
  refetch: any;
} {
  return showFavourites ? fetchBillPayments({ accountTypeId: 0 }) : fetchTopProducts();
}

function shouldRenderTiles(
  productList: any[],
  showFavourites: any,
  renderProductTile: React.FC<ProductTileProps>,
  isEditEnabled: boolean,
  t,
): unknown {
  return productList.length === 1 && showFavourites ? (
    <View style={styles.singTileStyle}>
      {renderProductTile({ item: productList[0], isEditEnabled, showFavourites, isAddEnabled: true })}
      <Text variant="sm-bold" textAlign="center" color={colors.neutral.darkMist} style={[, appStyles.marginTop(10)]}>
        {t('favourites.addDesc')}
      </Text>
    </View>
  ) : (
    <FlatList
      scrollEnabled={false}
      testID="fltLstTopProducts"
      style={styles.topProductsList}
      contentContainerStyle={[styles.fltLstContainerStyle]}
      numColumns={4}
      data={productList}
      keyExtractor={item => item?.id?.toString()}
      renderItem={({ item }) => (
        <View style={styles.boxAdjustmentStyle}>{renderProductTile({ item, isEditEnabled, showFavourites })}</View>
      )}
    />
  );
}

const TopProducts: FC = () => {
  const [openModal, setOpenModal] = useState(false);

  const [isFeatureAvailable] = useFeatureFlag({
    featureName: [FEATURE_FLAGS.FAV_HOMEPAGE],
  });

  const showFavourites = isFeatureAvailable[FEATURE_FLAGS.FAV_HOMEPAGE]?.show;

  const { isLoading, isError, data, isSuccess, refetch } = fetchTopOrFavourites(showFavourites);

  const { data: favData, isSuccess: favSuccess, refetch: fetchFavApi } = fetchFavourites(showFavourites); // 👈 only favourites API

  const isFocused = useIsFocused();
  const { t } = useTranslation('common');

  const addTile = {
    id: -1,
    name: 'Tambah',
    screenName: t('favourites.addTile'),
    availability: true,
    icon: <Ionicons name="add-circle" size={scale(30)} color={colors.secondary.orchid} />,
    isFavourite: true,
  };

  const others = {
    icon: lainnya,
    name: 'Lainnya',
    screenName: t('Lainnya'),
    id: -1,
    availability: true,
  };

  useEffect(() => {
    if (isFocused) refetch();
  }, [isFocused, showFavourites, refetch]);

  const [productList, setProductList] = React.useState<any[]>([]);
  const [notFavouriteProducts, setNotFavouriteProducts] = React.useState<any[]>([]);

  useEffect(() => {
    if (!showFavourites) return;

    if (favSuccess && favData?.list) {
      const favList = [...favData.list];
      if (favList.length < (favData?.maxFavorites || 8)) {
        favList.push(addTile);
      }
      setProductList(favList);
    }
  }, [favSuccess, favData]);

  useEffect(() => {
    if (!isSuccess || !data?.list) {
      setProductList([]);
      setNotFavouriteProducts([]);
      return;
    }

    if (showFavourites) {
      const notFavourites = transformProducts(data).notFavouriteProducts;

      setNotFavouriteProducts(notFavourites);
    } else {
      const list = [...data.list];
      const otherTileExists = list.some(item => item.id === -1);

      if (!otherTileExists) {
        list.push(others);
      }

      setProductList(list);
    }
  }, [isSuccess, data, showFavourites]);

  const handleRemoveProduct = (itemId: number) => {
    setProductList(prevFav => {
      const updatedFav = prevFav.filter(item => item.id !== itemId);
      const removedItem = prevFav.find(item => item.id === itemId);

      // Check if "Tambah" tile already exists
      const hasAddTile = updatedFav.some(item => item.id === -1);

      // Add "Tambah" if not present and under maxFavorites
      if (!hasAddTile && updatedFav.length < (favData?.maxFavorites || 8)) {
        updatedFav.push(addTile);
      }

      // Update notFavouriteProducts safely inside the same block
      if (removedItem) {
        setNotFavouriteProducts(prevNotFav => [...prevNotFav, { ...removedItem, isFavorites: false }]);
      }

      return updatedFav;
    });
  };

  const handleAddProduct = (itemId: number) => {
    setNotFavouriteProducts(prevNotFav => {
      const addedItem = prevNotFav.find(item => item.id === itemId);
      if (!addedItem) return prevNotFav;

      const updatedNotFav = prevNotFav.filter(item => item.id !== itemId);

      setProductList(prevFav => {
        // Remove the "Tambah" tile temporarily
        const addTileIndex = prevFav.findIndex(item => item.id === -1);
        const addTile = addTileIndex >= 0 ? prevFav.splice(addTileIndex, 1)[0] : null;

        // Add the new product to the front
        const newFav = [{ ...addedItem, isFavorites: true }, ...prevFav];

        // Only add "Tambah" tile if it doesn't exceed maxFavorites
        if (addTile && newFav.length < (favData?.maxFavorites || 8)) {
          newFav.push(addTile);
        }

        // If newFav still exceeds maxFavorites, trim the excess (without removing "Tambah")
        return newFav.slice(0, favData?.maxFavorites || 8);
      });

      return updatedNotFav;
    });
  };

  const renderProductTile = useCallback<FC<ProductTileProps>>(
    ({ item, isEditEnabled, showFavourites }) => (
      <BPProductTile
        bpCategoryProducts={item}
        source="home_page"
        isEditEnabled={isEditEnabled}
        onPressBPTile={() => {
          setOpenModal(prevState => !prevState);
        }}
        onRemoveSuccess={() => handleRemoveProduct(item.id)}
        showFavourites={showFavourites}
      />
    ),
    [showFavourites],
  );

  const [isEditEnabled, setIsEditEnabled] = React.useState(false);

  const handleToggleEdit = () => setIsEditEnabled(prev => !prev);

  useEffect(() => {
    if (!isFocused || productList.length === 1) setIsEditEnabled(false);
  }, [isFocused, productList]);

  useEffect(() => {
    if (showFavourites && isFocused) fetchFavApi();
  }, [showFavourites, isFocused]);

  if (isLoading) {
    return (
      <View flexDirection="row" style={appStyles.marginHorizontal(16)}>
        {Array.from({ length: 4 }).map(() => (
          <ShimmerBPTile key={Math.random()} />
        ))}
      </View>
    );
  }

  if (isError) return null;

  return (
    <View>
      <FavouriteHeader
        isEditEnabled={isEditEnabled}
        onToggleEdit={handleToggleEdit}
        showEdit={productList.length !== 1}
        t={t}
        show={showFavourites}
      />
      <AddFavProducts
        isVisible={openModal}
        t={t}
        data={notFavouriteProducts}
        handleVisible={() => setOpenModal(false)}
        onAddFavSuccess={itemId => handleAddProduct(itemId)}
        show={showFavourites}
      />
      <View style={appStyles.flexRow}>
        {shouldRenderTiles(productList, showFavourites, renderProductTile, isEditEnabled, t)}
      </View>
    </View>
  );
};

export default React.memo(TopProducts);
